import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDown,
  ChevronRight,
  FolderTree,
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  X,
  AlertTriangle,
  FolderOpen,
  Folder,
  Hash,
} from 'lucide-react';

interface Category {
  id: string;
  parent_id: string | null;
  name: string;
  level: 1 | 2 | 3;
  sort_order: number;
}

interface TreeNode extends Category {
  children: TreeNode[];
}

type ModalState =
  | { mode: 'create'; parentId: string | null; childLevel: 1 | 2 | 3 }
  | { mode: 'edit'; category: Category }
  | null;

function buildTree(categories: Category[]): TreeNode[] {
  const map = new Map<string, TreeNode>();
  for (const c of categories) {
    map.set(c.id, { ...c, children: [] });
  }
  const roots: TreeNode[] = [];
  for (const node of map.values()) {
    if (node.parent_id) {
      const parent = map.get(node.parent_id);
      if (parent) parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortRec = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => a.sort_order - b.sort_order);
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

export default function AdminEducationCategoriesTab() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<ModalState>(null);
  const [confirmDelete, setConfirmDelete] = useState<Category | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const tree = useMemo(() => buildTree(categories), [categories]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 3000);
    return () => clearTimeout(t);
  }, [message]);

  async function fetchCategories() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/admin/categories', { credentials: 'include' });
      if (!res.ok) throw new Error('카테고리 목록을 불러오지 못했습니다.');
      const json = await res.json();
      setCategories(json.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function expandAll() {
    setExpanded(new Set(categories.map((c) => c.id)));
  }

  function collapseAll() {
    setExpanded(new Set());
  }

  async function handleSubmitModal(name: string) {
    if (!modal) return;
    try {
      if (modal.mode === 'create') {
        const res = await fetch('/api/v1/admin/categories', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ parentId: modal.parentId, name }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || '생성에 실패했습니다.');
        if (modal.parentId) setExpanded((prev) => new Set(prev).add(modal.parentId!));
        setMessage({ type: 'success', text: '카테고리가 추가되었습니다.' });
      } else {
        const res = await fetch(`/api/v1/admin/categories/${modal.category.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || '수정에 실패했습니다.');
        setMessage({ type: 'success', text: '카테고리가 수정되었습니다.' });
      }
      setModal(null);
      await fetchCategories();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : '오류가 발생했습니다.' });
    }
  }

  async function handleConfirmDelete() {
    if (!confirmDelete) return;
    try {
      const res = await fetch(`/api/v1/admin/categories/${confirmDelete.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || '삭제에 실패했습니다.');
      setMessage({ type: 'success', text: '카테고리가 삭제되었습니다.' });
      setConfirmDelete(null);
      await fetchCategories();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : '오류가 발생했습니다.' });
      setConfirmDelete(null);
    }
  }

  function handleDragStart(e: DragStartEvent) {
    setActiveDragId(String(e.active.id));
  }

  async function handleDragEnd(e: DragEndEvent) {
    const draggedId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    setActiveDragId(null);
    if (!overId || draggedId === overId) return;

    const dragged = categories.find((c) => c.id === draggedId);
    const over = categories.find((c) => c.id === overId);
    if (!dragged || !over) return;

    // 같은 부모(형제) 사이에서만 순서 변경 허용. 레벨/부모 이동은 지원하지 않음.
    if (dragged.parent_id !== over.parent_id) {
      setMessage({ type: 'error', text: '같은 그룹(형제 카테고리) 안에서만 순서를 변경할 수 있습니다.' });
      return;
    }

    const siblings = categories
      .filter((c) => c.parent_id === dragged.parent_id)
      .sort((a, b) => a.sort_order - b.sort_order);
    const oldIndex = siblings.findIndex((c) => c.id === draggedId);
    const newIndex = siblings.findIndex((c) => c.id === overId);
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

    const reordered = arrayMove(siblings, oldIndex, newIndex);
    const orderedIds = reordered.map((c) => c.id);

    // Optimistic update
    const prev = categories;
    setCategories((cs) =>
      cs.map((c) => {
        const idx = orderedIds.indexOf(c.id);
        return idx === -1 ? c : { ...c, sort_order: idx };
      }),
    );

    try {
      const res = await fetch('/api/v1/admin/categories/reorder', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parentId: dragged.parent_id, orderedIds }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || '순서 변경에 실패했습니다.');
      setMessage({ type: 'success', text: '순서가 변경되었습니다.' });
    } catch (err) {
      setCategories(prev);
      setMessage({ type: 'error', text: err instanceof Error ? err.message : '오류가 발생했습니다.' });
    }
  }

  const draggedNode = activeDragId ? categories.find((c) => c.id === activeDragId) : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
              <FolderTree className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-primary">교육 카테고리 분류</h2>
              <p className="text-sm text-gray-600 mt-1">
                1차 → 2차 → 3차 트리 구조. 카테고리 헤더를 잡고 드래그하면 같은 부모(형제) 안에서 순서만 바꿀 수 있습니다. 다른 부모로 이동하거나 차수를 바꾸는 기능은 지원하지 않습니다.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700"
            >
              모두 펼치기
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700"
            >
              모두 접기
            </button>
            <button
              onClick={() => setModal({ mode: 'create', parentId: null, childLevel: 1 })}
              className="px-4 py-2 text-sm rounded-lg bg-accent text-white hover:bg-accent-600 font-medium flex items-center gap-1.5 shadow-sm"
            >
              <Plus className="w-4 h-4" />
              1차 카테고리 추가
            </button>
          </div>
        </div>
      </div>

      {/* Toast */}
      {message && (
        <div
          className={`px-4 py-3 rounded-lg text-sm border ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Tree */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">불러오는 중...</div>
        ) : error ? (
          <div className="p-12 text-center text-red-600">{error}</div>
        ) : tree.length === 0 ? (
          <div className="p-12 text-center text-gray-500">등록된 카테고리가 없습니다.</div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={tree.map((n) => n.id)} strategy={verticalListSortingStrategy}>
              <ul className="divide-y divide-gray-100">
                {tree.map((node) => (
                  <TreeRow
                    key={node.id}
                    node={node}
                    expanded={expanded}
                    onToggle={toggleExpand}
                    onAddChild={(parent) =>
                      setModal({
                        mode: 'create',
                        parentId: parent.id,
                        childLevel: (parent.level + 1) as 2 | 3,
                      })
                    }
                    onEdit={(c) => setModal({ mode: 'edit', category: c })}
                    onDelete={(c) => setConfirmDelete(c)}
                    activeDragId={activeDragId}
                  />
                ))}
              </ul>
            </SortableContext>
            <DragOverlay>
              {draggedNode ? (
                <div className="px-4 py-2 bg-white rounded-lg shadow-lg border-2 border-accent flex items-center gap-2 text-sm font-medium">
                  <GripVertical className="w-4 h-4 text-gray-400" />
                  <span className="text-primary">{draggedNode.name}</span>
                  <span className="text-xs text-gray-500">({draggedNode.level}차)</span>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Edit/Create Modal */}
      {modal && (
        <CategoryModal
          modal={modal}
          onClose={() => setModal(null)}
          onSubmit={handleSubmitModal}
        />
      )}

      {/* Delete Confirm */}
      {confirmDelete && (
        <DeleteConfirmModal
          category={confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
}

interface TreeRowProps {
  node: TreeNode;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onAddChild: (parent: Category) => void;
  onEdit: (c: Category) => void;
  onDelete: (c: Category) => void;
  activeDragId: string | null;
}

function TreeRow({
  node,
  expanded,
  onToggle,
  onAddChild,
  onEdit,
  onDelete,
  activeDragId,
}: TreeRowProps) {
  const isExpanded = expanded.has(node.id);
  const hasChildren = node.children.length > 0;
  const canHaveChildren = node.level < 3;

  // 같은 부모(형제) 사이에서만 순서 변경 가능한 sortable 아이템
  const sortable = useSortable({ id: node.id });
  const isDragging = activeDragId === node.id;
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  };

  const levelStyles = {
    1: {
      row: 'bg-primary-50/50 hover:bg-primary-50',
      pl: 'pl-3',
      icon: <Folder className="w-4 h-4 text-primary" />,
      iconOpen: <FolderOpen className="w-4 h-4 text-primary" />,
      name: 'font-bold text-primary text-base',
      badge: 'bg-primary text-white',
    },
    2: {
      row: 'bg-white hover:bg-gray-50 border-l-4 border-accent-200',
      pl: 'pl-10',
      icon: <Folder className="w-4 h-4 text-accent-600" />,
      iconOpen: <FolderOpen className="w-4 h-4 text-accent-600" />,
      name: 'font-semibold text-gray-800 text-[15px]',
      badge: 'bg-accent-100 text-accent-700',
    },
    3: {
      row: 'bg-white hover:bg-gray-50',
      pl: 'pl-20',
      icon: <Hash className="w-3.5 h-3.5 text-gray-400" />,
      iconOpen: <Hash className="w-3.5 h-3.5 text-gray-400" />,
      name: 'text-gray-700 text-sm',
      badge: 'bg-gray-100 text-gray-600',
    },
  } as const;
  const s = levelStyles[node.level];

  return (
    <li ref={sortable.setNodeRef} style={style}>
      <div
        className={`group flex items-center gap-2 py-2.5 pr-3 transition-colors ${s.row} ${s.pl} ${
          sortable.isOver ? 'ring-2 ring-accent ring-inset bg-accent-50' : ''
        } ${isDragging ? 'opacity-40' : ''}`}
      >
        {/* Drag handle */}
        <button
          {...sortable.listeners}
          {...sortable.attributes}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-0.5"
          title="드래그하여 순서 변경"
          aria-label="드래그 핸들"
        >
          <GripVertical className="w-4 h-4" />
        </button>

        {/* Expand/Collapse */}
        {canHaveChildren ? (
          <button
            onClick={() => onToggle(node.id)}
            className="text-gray-500 hover:text-gray-800 p-0.5"
            aria-label={isExpanded ? '접기' : '펼치기'}
          >
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        ) : (
          <span className="w-5" />
        )}

        {/* Icon + Name */}
        <span className="flex items-center gap-2 flex-1 min-w-0">
          <span className="flex-shrink-0">{isExpanded && hasChildren ? s.iconOpen : s.icon}</span>
          <span className={`truncate ${s.name}`}>{node.name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${s.badge} flex-shrink-0`}>
            {node.level}차
          </span>
          {hasChildren && (
            <span className="text-xs text-gray-400 flex-shrink-0">({node.children.length})</span>
          )}
        </span>

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {canHaveChildren && (
            <button
              onClick={() => onAddChild(node)}
              className="px-2 py-1 text-xs rounded border border-gray-200 hover:bg-primary hover:text-white hover:border-primary text-gray-600 flex items-center gap-1"
              title={`${node.level + 1}차 추가`}
            >
              <Plus className="w-3 h-3" />
              {node.level + 1}차
            </button>
          )}
          <button
            onClick={() => onEdit(node)}
            className="p-1.5 rounded hover:bg-gray-200 text-gray-500"
            title="이름 편집"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDelete(node)}
            className="p-1.5 rounded hover:bg-red-100 hover:text-red-600 text-gray-500"
            title="삭제"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {isExpanded && hasChildren && (
        <SortableContext items={node.children.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          <ul className="divide-y divide-gray-100">
            {node.children.map((child) => (
              <TreeRow
                key={child.id}
                node={child}
                expanded={expanded}
                onToggle={onToggle}
                onAddChild={onAddChild}
                onEdit={onEdit}
                onDelete={onDelete}
                activeDragId={activeDragId}
              />
            ))}
          </ul>
        </SortableContext>
      )}
    </li>
  );
}

interface CategoryModalProps {
  modal: NonNullable<ModalState>;
  onClose: () => void;
  onSubmit: (name: string) => void | Promise<void>;
}

function CategoryModal({ modal, onClose, onSubmit }: CategoryModalProps) {
  const [name, setName] = useState(modal.mode === 'edit' ? modal.category.name : '');
  const [submitting, setSubmitting] = useState(false);

  const titleLevel = modal.mode === 'create' ? modal.childLevel : modal.category.level;
  const title =
    modal.mode === 'create'
      ? `${titleLevel}차 카테고리 추가`
      : `${titleLevel}차 카테고리 이름 수정`;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(name.trim());
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-primary">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">이름</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="카테고리 이름"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              autoFocus
              required
              maxLength={100}
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={!name.trim() || submitting}
              className="px-4 py-2 text-sm rounded-lg bg-accent text-white hover:bg-accent-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeleteConfirmModalProps {
  category: Category;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}

function DeleteConfirmModal({ category, onClose, onConfirm }: DeleteConfirmModalProps) {
  const [submitting, setSubmitting] = useState(false);
  async function handleConfirm() {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <h3 className="font-bold text-primary">카테고리 삭제</h3>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-700">
            <span className="font-semibold text-gray-900">"{category.name}"</span> 카테고리를 삭제하시겠습니까?
          </p>
          <p className="text-xs text-gray-500">
            하위 항목이 있을 경우 삭제할 수 없습니다. 먼저 하위 항목을 삭제해 주세요.
          </p>
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700"
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting}
            className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 font-medium disabled:opacity-50"
          >
            {submitting ? '삭제 중...' : '삭제'}
          </button>
        </div>
      </div>
    </div>
  );
}
